import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { useUnlockUserAccess } from '@/common/hooks/queries/useUnlockUserAccess'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { cn } from '@/common/utils/cn'
import { formatDateTime } from '@/common/utils/formatters'
import { InfoIcon } from 'lucide-react'
import { useState } from 'react'

interface UnlockUserAccessProps {
  userId: string
  failedAttempts: number
  lockedAt: string | null
  entity: 'client' | 'establishment' | ''
  operationKlassToCheckPermission: string
  onSuccess?: () => void
}

export const UnlockUserAccess = ({
  userId,
  failedAttempts,
  lockedAt,
  entity,
  operationKlassToCheckPermission,
  onSuccess,
}: UnlockUserAccessProps) => {
  const { hasUserPermission } = useUserPermissions()

  const userHasPermissionToUnlockUserAccess = hasUserPermission(operationKlassToCheckPermission)

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)

  const { mutate: unlockUserAccess, isPending: unlockUserAccessIsPending } = useUnlockUserAccess({
    userId,
    entity,
    onSuccess: () => {
      setIsOpen(false)
      setIsError(false)
      setMessages(['Acesso desbloqueado com sucesso.'])
    },
    onError: (error) => {
      setIsOpen(false)
      setIsError(true)
      setMessages((error.response?.data as { errors: { base: string[] } })?.errors?.base || null)
    },
  })

  return (
    <div
      className={cn(
        'flex w-full justify-between items-center bg-white-100 rounded-sm p-4',
        !!lockedAt && 'bg-danger-op',
      )}
    >
      <div className="flex items-center gap-x-2">
        <InfoIcon className="text-yellow-600 shrink-0" />

        <div className="flex flex-col gap-y-1">
          <p className="font-bold text-sm leading-3">
            {!!lockedAt
              ? 'Usuário bloqueado devido a múltiplas tentativas incorretas de acesso'
              : 'Usuário com tentativas incorretas de acesso'}
          </p>

          <p className="font-light text-xs italic leading-3">
            {!!lockedAt
              ? `Bloqueado dia ${formatDateTime(new Date(lockedAt))} após ${failedAttempts} tentativas incorretas.`
              : `Este usuário possui ${failedAttempts} tentativa${failedAttempts > 1 ? 's' : ''} incorreta${
                  failedAttempts > 1 ? 's' : ''
                } de acesso.`}
          </p>
        </div>
      </div>

      {!!lockedAt && (
        <Modal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          Trigger={
            <Button
              name="unlock_company_user_access"
              operationKlassToCheckPermission={operationKlassToCheckPermission}
              disabled={unlockUserAccessIsPending}
            >
              Desbloquear acesso
            </Button>
          }
          triggerDisabled={!userHasPermissionToUnlockUserAccess}
          useCancellableFooter
          showClose={false}
          title="Você deseja realmente desbloquear o acesso?"
          subtitle="Ao clicar em confirmar, o acesso do usuário será reativado."
          subtitleClassName="mt-1 text-md text-white-300"
          confirmButtonClassName="bg-blue-700"
          onConfirm={() => unlockUserAccess()}
        />
      )}

      {messages && (
        <ModalMessageSimple messages={messages} setMessages={setMessages} isError={isError} onClose={onSuccess} />
      )}
    </div>
  )
}
